import { Link } from "react-router-dom";
import { Linkedin, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { TEAM } from "../data/team";

export const TeamCard = ({ member, index = 0, compact = false }) => (
  <Reveal delay={Math.min(index * 0.07, 0.3)} className="h-full">
    <div
      className="group card-line flex h-full flex-col items-center p-6 text-center transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-500/60 hover:shadow-xl hover:shadow-blue-600/10"
      data-testid={`team-card-${member.initials.toLowerCase()}`}
    >
      {member.photo ? (
        <img
          src={member.photo}
          alt={`Portrait of ${member.name}`}
          className="h-20 w-20 rounded-2xl object-cover shadow-lg shadow-blue-600/20 transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <span
          className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#29abe2] via-[#1b6fc4] to-[#123a75] font-display text-xl font-bold text-white shadow-lg shadow-blue-600/20 transition-transform duration-300 group-hover:scale-105"
          aria-hidden="true"
        >
          {member.initials}
        </span>
      )}
      <h3 className="mt-4 font-display text-base font-bold tracking-tight text-slate-900">
        {member.name}
      </h3>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
        {member.role}
      </p>
      {!compact && (
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{member.focus}</p>
      )}
      {member.sample && (
        <span className="mt-3 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
          Sample
        </span>
      )}
      {member.linkedin && (
        <a
          href={member.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${member.name} on LinkedIn`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 transition-colors hover:text-blue-900"
          data-testid={`team-linkedin-${member.initials.toLowerCase()}`}
        >
          <Linkedin size={14} aria-hidden="true" /> LinkedIn
        </a>
      )}
    </div>
  </Reveal>
);

export default function Team() {
  const founder = TEAM.find((m) => m.featured);
  const rest = TEAM.filter((m) => !m.featured);
  return (
    <>
      <SEO
        title="Team | ZasyaOnline"
        description="Meet the people behind ZasyaOnline's products — MoveOS, myBantu, LearnSphere and CraneSoftwares — and its consulting and delivery practice."
        path="/team"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="team-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <span className="eyebrow">Team</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              The people behind the products.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              A product company is its people. Ours design, build, ship and support
              the software on this site — and deliver the consulting engagements
              behind it.
            </p>
          </Reveal>
        </div>
      </section>

      {founder && (
        <section className="pb-16 sm:pb-20" data-testid="team-founder-spotlight">
          <div className="container-x">
            <Reveal>
              <div className="card-line grain relative grid items-center gap-8 overflow-hidden p-8 sm:p-12 lg:grid-cols-12">
                <div
                  className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-600/10 blur-3xl"
                  aria-hidden="true"
                />
                <div className="lg:col-span-4">
                  <div className="clip-frame mx-auto max-w-xs overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/10">
                    <img
                      src={founder.photo}
                      alt={`Portrait of ${founder.name}`}
                      className="aspect-square w-full object-cover"
                      loading="eager"
                      data-testid="team-founder-photo"
                    />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  {founder.tag && (
                    <span className="mb-3 inline-block rounded-full bg-blue-600 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white" data-testid="team-founder-tag">
                      {founder.tag}
                    </span>
                  )}
                  <span className="block font-mono text-[11px] uppercase tracking-[0.22em] text-teal-700">
                    {founder.role}
                  </span>
                  <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                    {founder.name}
                  </h2>
                  {founder.quote && (
                    <blockquote
                      className="mt-5 max-w-2xl border-l-2 border-blue-600 pl-5 font-display text-lg font-semibold leading-relaxed text-slate-800"
                      data-testid="team-founder-quote"
                    >
                      &ldquo;{founder.quote}&rdquo;
                    </blockquote>
                  )}
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                    {founder.bio}
                  </p>
                  {founder.linkedin && (
                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary mt-6 !px-5 !py-2.5"
                      data-testid="team-founder-linkedin"
                    >
                      <Linkedin size={15} aria-hidden="true" /> Connect on LinkedIn
                    </a>
                  )}
                  <div>
                    <Link
                      to="/insights/about-the-founder"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 transition-colors hover:text-blue-900"
                      data-testid="team-founder-story-link"
                    >
                      Read the founder's story <ArrowRight size={15} aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-white py-16 sm:py-20" data-testid="team-grid">
        <div className="container-x grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((m, i) => (
            <TeamCard key={m.initials} member={m} index={i} />
          ))}
        </div>
      </section>

      <CTASection
        eyebrow="Work with the team"
        title="Talk to the people who build it."
        body="Every enquiry reaches the team directly — no switchboards, no generic inboxes."
      />
    </>
  );
}
